import React,{useEffect,useMemo,useState} from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import {useSupabaseData} from '../contexts/SupabaseDataContext';
import {useSupabaseAuth} from '../contexts/SupabaseAuthContext';
import {
  getMissingRequiredOptionGroups,
  getOptionPriceAdjustment,
  getOptionSelectionLabel,
  getOptionUnitPriceAdjustment,
  normalizeOfferingOptions
} from '../services/offeringOptionsService';
import {
  getAvailableFulfillmentTimes,
  getValidOrderDates,
  isOrderDateAllowed
} from '../services/sellerAvailabilityService';
import {
  CRA_BAKERY_CLASSIFICATIONS,
  getCraBakeryBasketStatus
} from '../services/craBakeryTaxService';
import {createOrderCheckout} from '../services/stripePaymentService';
import SafeIcon from '../common/SafeIcon';
import AllergenLabels from './common/AllergenLabels';
import IngredientDisclosure from './common/IngredientDisclosure';
import CustomizationPhotoUpload from './orders/CustomizationPhotoUpload';
import * as FiIcons from 'react-icons/fi';

const {
  FiX,
  FiCalendar,
  FiCheckCircle,
  FiLoader,
  FiPlus,
  FiMinus,
  FiShield,
  FiTruck,
  FiMapPin
}=FiIcons;

const numericPrice=(price)=>{
  if (typeof price==='number') return price;

  return Number.parseFloat(
    String(price || '').replace(/[^0-9.-]/g,'')
  ) || 0;
};

const formatPrice=(price)=>`$${Number(price || 0).toFixed(2)}`;

const fulfillmentTimeOptions=Array.from({length:48},(_,index)=>{
  const hour=Math.floor(index/2);
  const minute=index%2===0 ? 0 : 30;
  const value=`${String(hour).padStart(2,'0')}:${String(minute).padStart(2,'0')}`;
  const label=new Date(2000,0,1,hour,minute).toLocaleTimeString([],{
    hour:'numeric',
    minute:'2-digit'
  });

  return {value,label};
});

const createGroup=(quantity,selection={})=>({
  id:`group-${Date.now()}-${Math.random()}`,
  quantity,
  selection,
  special_instructions:'',
  custom_photo_urls:[]
});

const getGroups=(item)=>{
  if (
    item.customization_mode==='split'
    && Array.isArray(item.customization_groups)
    && item.customization_groups.length
  ) {
    return item.customization_groups;
  }

  return [{
    quantity:item.quantity,
    selection:item.selection || {},
    special_instructions:item.special_instructions || ''
  }];
};

const SplitGroups=({
  offering,
  item,
  options,
  userId,
  sellerId,
  onChange
})=>{
  const groups=item.customization_groups || [
    createGroup(item.quantity,item.selection)
  ];

  const updateGroup=(id,updates)=>{
    let nextGroups=groups.map((group)=>(
      group.id===id ? {...group,...updates} : group
    ));

    if (updates.quantity!==undefined) {
      const selectedQuantity=Math.max(
        1,
        Number(updates.quantity) || 1
      );
      let remainingQuantity=Math.max(
        0,
        Number(item.quantity)-selectedQuantity
      );
      const otherGroups=nextGroups.filter((group)=>group.id!==id);

      nextGroups=nextGroups.map((group)=>{
        if (group.id===id) {
          return {
            ...group,
            quantity:selectedQuantity
          };
        }

        const groupsAfter=otherGroups.filter(
          (entry)=>entry.id!==group.id
        ).length;
        const maximumForGroup=Math.max(
          1,
          remainingQuantity-groupsAfter
        );
        const groupQuantity=Math.min(
          maximumForGroup,
          Math.max(1,Number(group.quantity) || 1)
        );

        remainingQuantity-=groupQuantity;

        return {
          ...group,
          quantity:groupQuantity
        };
      });
    }

    onChange({
      ...item,
      customization_groups:nextGroups
    });
  };

  const updateSelection=(id,name,value)=>{
    const group=groups.find((entry)=>entry.id===id);

    updateGroup(id,{
      selection:{
        ...(group?.selection || {}),
        [name]:value
      }
    });
  };

  const addGroup=()=>{
    const source=groups.find((group)=>Number(group.quantity)>1);

    if (!source) return;

    onChange({
      ...item,
      customization_groups:[
        ...groups.map((group)=>(
          group.id===source.id
            ? {...group,quantity:Number(group.quantity)-1}
            : group
        )),
        createGroup(1)
      ]
    });
  };

  const removeGroup=(id)=>{
    if (groups.length===1) return;

    const removed=groups.find((group)=>group.id===id);
    const remaining=groups.filter((group)=>group.id!==id);

    onChange({
      ...item,
      customization_groups:remaining.map((group,index)=>(
        index===0
          ? {...group,quantity:Number(group.quantity)+Number(removed?.quantity || 0)}
          : group
      ))
    });
  };

  return (
    <div
      className="mt-5 space-y-4 border-t border-indigo-100 pt-5"
      onClick={(event)=>event.stopPropagation()}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          Split customization groups
        </p>
        <button
          type="button"
          onClick={addGroup}
          disabled={!groups.some((group)=>Number(group.quantity)>1)}
          className="flex items-center rounded-xl bg-indigo-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-indigo-600 disabled:opacity-40"
        >
          <SafeIcon icon={FiPlus} className="mr-2" />
          Add group
        </button>
      </div>

      {groups.map((group,index)=>(
        <div
          key={group.id}
          className="rounded-2xl border border-indigo-100 bg-white p-4"
        >
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-black uppercase tracking-widest text-slate-700">
              Group {index+1}
            </p>

            <div className="flex items-center gap-2">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                Items
                <input
                  type="number"
                  min="1"
                  value={group.quantity}
                  onChange={(event)=>{
                    const otherQuantity=groups
                      .filter((entry)=>entry.id!==group.id)
                      .reduce(
                        (total,entry)=>total+Number(entry.quantity || 0),
                        0
                      );
                    const maximum=Math.max(
                      1,
                      Number(item.quantity)-otherQuantity
                    );

                    updateGroup(group.id,{
                      quantity:Math.min(
                        maximum,
                        Math.max(1,Number(event.target.value) || 1)
                      )
                    });
                  }}
                  className="ml-2 w-16 rounded-lg border border-slate-200 px-2 py-2 text-center text-xs font-black"
                />
              </label>

              {groups.length>1 && (
                <button
                  type="button"
                  onClick={()=>removeGroup(group.id)}
                  className="rounded-lg px-2 py-2 text-[10px] font-black uppercase text-red-500"
                >
                  Remove
                </button>
              )}
            </div>
          </div>

          {options.map((optionGroup)=>(
            <div key={optionGroup.name} className="mb-4">
              <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-500">
                {optionGroup.name}
                {optionGroup.required && (
                  <span className="ml-1 text-red-500">*</span>
                )}
              </label>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {optionGroup.values.map((option)=>(
                  <label
                    key={option.label}
                    className="flex items-center justify-between rounded-xl bg-slate-50 p-3 text-xs font-bold text-slate-700"
                  >
                    <span>
                      <input
                        type={
                          optionGroup.multiple
                            ? 'checkbox'
                            : 'radio'
                        }
                        name={`${offering.id}-${group.id}-${optionGroup.name}`}
                        checked={
                          optionGroup.multiple
                            ? Array.isArray(
                              group.selection?.[optionGroup.name]
                            )
                              && group.selection[
                                optionGroup.name
                              ].includes(option.label)
                            : group.selection?.[
                              optionGroup.name
                            ]===option.label
                        }
                        onChange={()=>{
                          if (!optionGroup.multiple) {
                            updateSelection(
                              group.id,
                              optionGroup.name,
                              option.label
                            );
                            return;
                          }

                          const current=Array.isArray(
                            group.selection?.[optionGroup.name]
                          )
                            ? group.selection[optionGroup.name]
                            : [];

                          updateSelection(
                            group.id,
                            optionGroup.name,
                            current.includes(option.label)
                              ? current.filter(
                                (value)=>value!==option.label
                              )
                              : [...current,option.label]
                          );
                        }}
                        className="mr-2 text-indigo-600"
                      />
                      {option.label}
                    </span>

                    {option.price!==0 && (
                      <span className="text-indigo-600">
                        {option.price>0 ? '+' : ''}
                        {formatPrice(option.price)}
                        <span className="ml-1 text-[9px] text-slate-400">
                          {option.pricing_mode==='once_per_order'
                            ? 'group'
                            : 'each'}
                        </span>
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          ))}

          <textarea
            value={group.special_instructions || ''}
            onChange={(event)=>updateGroup(group.id,{
              special_instructions:event.target.value
            })}
            rows="2"
            placeholder="Special instructions for this group"
            className="w-full rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs font-medium outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <div className="mt-4">
            <CustomizationPhotoUpload
              userId={userId}
              sellerId={sellerId}
              offeringId={offering.id}
              groupId={group.id}
              images={group.custom_photo_urls}
              onImagesChange={(custom_photo_urls)=>updateGroup(
                group.id,
                {custom_photo_urls}
              )}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const OrderFormModal=({
  seller,
  offerings,
  initialOffering,
  onClose
})=>{
  const {user}=useSupabaseAuth();
  const {createOrder}=useSupabaseData();
  const paymentMode=seller?.policy_settings?.paymentMode || 'seller_direct';

  const [selectedItems,setSelectedItems]=useState(()=>{
    if (!initialOffering) return {};

    return {
      [initialOffering.id]:{
        quantity:1,
        selection:{},
        customization_mode:'same',
        customization_groups:[
          createGroup(1)
        ],
        special_instructions:'',
        custom_photo_urls:[]
      }
    };
  });

  const bakeryStatus=useMemo(
    ()=>getCraBakeryBasketStatus(selectedItems,offerings),
    [selectedItems,offerings]
  );

  const minDate=useMemo(()=>{
    const date=new Date();

    date.setDate(
      date.getDate()+Number(seller?.min_order_window || 0)
    );

    return date.toISOString().split('T')[0];
  },[seller?.min_order_window]);

  const validDates=useMemo(()=>getValidOrderDates({
    minOrderWindow:seller?.min_order_window,
    weeklyAvailability:seller?.weekly_availability,
    blackoutDates:seller?.blackout_dates
  }),[
    seller?.min_order_window,
    seller?.weekly_availability,
    seller?.blackout_dates
  ]);

  const [formData,setFormData]=useState({
    requestedDate:minDate,
    requestedTime:'',
    deliveryMethod:'pickup',
    orderNotes:''
  });
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [submitted,setSubmitted]=useState(false);

  const validTimes=useMemo(()=>getAvailableFulfillmentTimes(
    formData.requestedDate,
    seller?.weekly_availability,
    fulfillmentTimeOptions
  ),[
    formData.requestedDate,
    seller?.weekly_availability
  ]);

  const pickupValidDates=useMemo(()=>getValidOrderDates({
    minOrderWindow:seller?.min_order_window,
    weeklyAvailability:seller?.pickup_weekly_availability,
    blackoutDates:seller?.pickup_blackout_dates
  }),[
    seller?.min_order_window,
    seller?.pickup_weekly_availability,
    seller?.pickup_blackout_dates
  ]);

  const pickupValidTimes=useMemo(()=>getAvailableFulfillmentTimes(
    formData.requestedDate,
    seller?.pickup_weekly_availability,
    fulfillmentTimeOptions
  ),[
    formData.requestedDate,
    seller?.pickup_weekly_availability
  ]);

  const effectiveValidDates=useMemo(()=>(
    formData.deliveryMethod==='pickup'
      ? validDates.filter((date)=>pickupValidDates.includes(date))
      : validDates
  ),[
    formData.deliveryMethod,
    pickupValidDates,
    validDates
  ]);

  const effectiveValidTimes=useMemo(()=>(
    formData.deliveryMethod==='pickup'
      ? validTimes.filter(({value})=>pickupValidTimes.some(
        (pickupTime)=>pickupTime.value===value
      ))
      : validTimes
  ),[
    formData.deliveryMethod,
    pickupValidTimes,
    validTimes
  ]);

  useEffect(()=>{
    if (effectiveValidDates.includes(formData.requestedDate)) return;

    setFormData((current)=>({
      ...current,
      requestedDate:effectiveValidDates[0] || '',
      requestedTime:''
    }));
  },[formData.requestedDate,effectiveValidDates]);

  useEffect(()=>{
    if (
      !formData.requestedTime
      || effectiveValidTimes.some(({value})=>value===formData.requestedTime)
    ) {
      return;
    }

    setFormData((current)=>({
      ...current,
      requestedTime:''
    }));
  },[formData.requestedTime,effectiveValidTimes]);

  const toggleItem=(offering)=>{
    setSelectedItems((current)=>{
      const next={...current};

      if (next[offering.id]) {
        delete next[offering.id];
      } else {
        next[offering.id]={
          quantity:1,
          selection:{},
          customization_mode:'same',
          customization_groups:[
            createGroup(1)
          ],
          special_instructions:'',
          custom_photo_urls:[]
        };
      }

      return next;
    });
  };

  const updateQuantity=(id,delta)=>{
    setSelectedItems((current)=>{
      const item=current[id];
      const quantity=Math.max(
        1,
        (current[id]?.quantity || 1)+delta
      );

      if (item?.customization_mode!=='split') {
        return {
          ...current,
          [id]:{
            ...current[id],
            quantity,
            customization_groups:current[id]?.customization_groups?.map(
              (group,index)=>index===0
                ? {...group,quantity}
                : group
            )
          }
        };
      }

      const difference=quantity-(item.quantity || 1);

      return {
        ...current,
        [id]:{
          ...current[id],
          quantity,
          customization_groups:(item.customization_groups || []).map(
            (group,index)=>index===0
              ? {...group,quantity:Math.max(1,group.quantity+difference)}
              : group
          )
        }
      };
    });
  };

  const updateSelection=(offering,group,value)=>{
    setSelectedItems((current)=>({
      ...current,
      [offering.id]:{
        ...current[offering.id],
        selection:{
          ...current[offering.id].selection,
          [group]:value
        }
      }
    }));
  };

  const updateCustomization=(offering,item)=>{
    setSelectedItems((current)=>({
      ...current,
      [offering.id]:item
    }));
  };

  const subtotal=useMemo(()=>{
    return Object.entries(selectedItems).reduce(
      (total,[id,item])=>{
        const offering=offerings.find((entry)=>entry.id===id);

        if (!offering) return total;

        const base=numericPrice(offering.base_price);

        return total+getGroups(item).reduce((groupTotal,group)=>{
          return groupTotal
            +(base*Number(group.quantity || 0))
            +getOptionPriceAdjustment(
              offering.options,
              group.selection,
              group.quantity
            );
        },0);
      },
      0
    );
  },[offerings,selectedItems]);

  const deliveryFee=(
    formData.deliveryMethod==='delivery'
    && seller.is_delivery_enabled
  )
    ? numericPrice(seller.delivery_charge)
    : 0;

  const totalPrice=subtotal+deliveryFee;

  const handleSubmit=async(event)=>{
    event.preventDefault();
    event.stopPropagation();

    if (
      loading
      || submitted
      || !user
      || !Object.keys(selectedItems).length
    ) {
      return;
    }

    if (!bakeryStatus.minimumMet) {
      setError(
        `Select at least six qualifying servings from ${seller.business_name}. You can mix and match qualifying products; quantities do not need to be multiples of six.`
      );
      return;
    }

    if (paymentMode==='platform' && bakeryStatus.checkoutIssue) {
      setError(bakeryStatus.checkoutIssue);
      return;
    }

    if (
      !effectiveValidDates.includes(formData.requestedDate)
      || !isOrderDateAllowed({
        date:formData.requestedDate,
        minOrderWindow:seller.min_order_window,
        weeklyAvailability:seller.weekly_availability,
        blackoutDates:seller.blackout_dates
      })
      || (
        formData.deliveryMethod==='pickup'
        && !pickupValidDates.includes(formData.requestedDate)
      )
    ) {
      setError('Choose an order date that meets the seller availability and minimum notice.');
      return;
    }

    if (!effectiveValidTimes.some(({value})=>value===formData.requestedTime)) {
      setError('Choose an available time for the selected date.');
      return;
    }

    const missingGroups=Object.entries(selectedItems)
      .flatMap(([id,item])=>{
        const offering=offerings.find((entry)=>entry.id===id);

        return offering
          ? getGroups(item).flatMap((group,index)=>(
            getMissingRequiredOptionGroups(
              offering.options,
              group.selection
            ).map((name)=>`${offering.title} group ${index+1}: ${name}`)
          ))
          : [];
      });

    const invalidGroups=Object.entries(selectedItems).some(
      ([,item])=>item.customization_mode==='split'
        && item.customization_groups.reduce(
          (total,group)=>total+Number(group.quantity || 0),
          0
        )!==Number(item.quantity)
    );

    if (missingGroups.length>0) {
      setError(
        `Please select the required options: ${missingGroups.join(', ')}.`
      );
      return;
    }

    if (invalidGroups) {
      setError(
        'Each split customization group must add up to the item quantity.'
      );
      return;
    }

    setLoading(true);
    setError('');

    try {
      const items=Object.entries(selectedItems).flatMap(
        ([id,item])=>{
          const offering=offerings.find(
            (entry)=>entry.id===id
          );

          return getGroups(item).map((group,index)=>{
            const base=numericPrice(offering.base_price);
            const unitAdjustment=getOptionUnitPriceAdjustment(
              offering.options,
              group.selection
            );
            const orderAdjustment=getOptionPriceAdjustment(
              offering.options,
              group.selection,
              group.quantity
            );
            const unitPrice=base+unitAdjustment;

            return {
              offering_id:id,
              title:offering.title,
              quantity:Number(group.quantity),
              price:formatPrice(unitPrice),
              option_adjustment:orderAdjustment,
              image:offering.images?.[0],
              ingredients:offering.ingredients || '',
              allergens:offering.allergens || '',
              options:group.selection,
              option_summary:getOptionSelectionLabel(
                group.selection
              ),
              customization_mode:item.customization_mode || 'same',
              customization_group:index+1,
              special_instructions:group.special_instructions || '',
              custom_photo_urls:group.custom_photo_urls
                || item.custom_photo_urls
                || []
            };
          });
        }
      );

      const createdOrder=await createOrder({
        buyer_id:user.id,
        seller_id:seller.id,
        items,
        total_amount:totalPrice,
        delivery_fee:deliveryFee,
        requested_date:formData.requestedDate,
        requested_time:formData.requestedTime,
        delivery_method:formData.deliveryMethod,
        order_notes:formData.orderNotes,
        payment_mode:paymentMode,
        status:'pending'
      });

      if (paymentMode==='platform') {
        const checkout=await createOrderCheckout(
          createdOrder.id,
          '/orders'
        );

        if (!checkout?.url) {
          throw new Error('Stripe did not return a checkout URL.');
        }

        window.location.assign(checkout.url);
        return;
      }

      setLoading(false);
      setSubmitted(true);
    } catch (submitError) {
      setError(
        submitError.message || 'Unable to submit this order.'
      );
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      onClick={(event)=>{
        if (event.target===event.currentTarget && !loading) {
          onClose();
        }
      }}
    >
      <motion.div
        initial={{opacity:0,scale:0.95,y:20}}
        animate={{opacity:1,scale:1,y:0}}
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto overscroll-contain rounded-[3rem] border border-slate-100 bg-white shadow-2xl [scrollbar-gutter:stable]"
        onClick={(event)=>event.stopPropagation()}
      >
        {submitted ? (
          <div className="p-10 text-center md:p-14">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
              <SafeIcon
                icon={FiCheckCircle}
                className="text-5xl text-emerald-600"
              />
            </div>

            <h2 className="mt-8 text-3xl font-black tracking-tighter text-slate-900">
              Order request sent
            </h2>

            <p className="mx-auto mt-4 max-w-md text-base font-medium leading-relaxed text-slate-500">
              Your request has been sent to {seller.business_name}.
              Please wait for the seller to accept your order.
            </p>

            <div className="mt-8 rounded-2xl bg-indigo-50 p-5 text-sm font-bold leading-relaxed text-indigo-700">
              You can check the request status anytime from your Orders page.
            </div>

            <button
              type="button"
              onClick={onClose}
              className="mt-8 w-full rounded-[1.5rem] bg-indigo-600 py-5 text-sm font-black uppercase tracking-[0.2em] text-white shadow-xl shadow-indigo-100 transition hover:bg-indigo-700"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-50 bg-white p-10">
              <div>
                <h2 className="text-3xl font-black tracking-tighter text-slate-900">
                  Place <span className="text-indigo-600">Order</span>
                </h2>
                <p className="mt-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                  {seller.business_name}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="rounded-2xl p-3 text-slate-400 hover:bg-slate-50 disabled:opacity-50"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-8 p-10"
            >
              {error && (
                <div className="rounded-2xl border border-red-100 bg-red-50 p-4 text-sm font-bold text-red-700">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">
                  Select Items
                </label>

                {offerings.map((offering)=>{
                  const item=selectedItems[offering.id];
                  const options=normalizeOfferingOptions(
                    offering.options
                  );
                  const classification=offering.cra_bakery_tax_classification
                    || 'needs_review';

                  return (
                    <div
                      key={offering.id}
                      role="checkbox"
                      tabIndex={0}
                      aria-checked={Boolean(item)}
                      onClick={()=>toggleItem(offering)}
                      onKeyDown={(event)=>{
                        if (
                          event.target.closest?.(
                            'input, textarea, select, button, a, [contenteditable="true"]'
                          )
                        ) {
                          return;
                        }

                        if (
                          event.key==='Enter'
                          || event.key===' '
                        ) {
                          event.preventDefault();
                          toggleItem(offering);
                        }
                      }}
                      className={`cursor-pointer rounded-2xl border p-4 transition ${
                        item
                          ? 'border-indigo-600 bg-indigo-50/30 ring-2 ring-indigo-100'
                          : 'border-slate-100 bg-slate-50 hover:border-indigo-300 hover:bg-indigo-50/30'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="h-12 w-12 overflow-hidden rounded-xl bg-white">
                          <img
                            src={offering.images?.[0]}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            width="48"
                            height="48"
                            className="h-full w-full object-cover"
                          />
                        </div>

                        <div className="ml-4 flex-1">
                          <h4 className="text-sm font-black text-slate-900">
                            {offering.title}
                          </h4>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">
                            {formatPrice(
                              numericPrice(offering.base_price)
                            )}
                          </p>
                        </div>

                        {item && (
                          <div
                            className="flex items-center rounded-xl border border-indigo-100 bg-white p-1"
                            onClick={(clickEvent)=>clickEvent.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={()=>updateQuantity(
                                offering.id,
                                -1
                              )}
                              className="p-2 text-indigo-600"
                            >
                              <SafeIcon icon={FiMinus} />
                            </button>

                            <span className="w-7 text-center text-xs font-black">
                              {item.quantity}
                            </span>

                            <button
                              type="button"
                              onClick={()=>updateQuantity(
                                offering.id,
                                1
                              )}
                              className="p-2 text-indigo-600"
                            >
                              <SafeIcon icon={FiPlus} />
                            </button>
                          </div>
                        )}
                      </div>

                      <p className="mt-3 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                        Seller-declared tax category: {CRA_BAKERY_CLASSIFICATIONS[classification] || CRA_BAKERY_CLASSIFICATIONS.needs_review}
                      </p>
                      {classification === 'six_serving' && (
                        <p className="mt-1 text-xs font-medium text-slate-600">
                          {Number(offering.cra_servings_per_unit) || 1} qualifying serving(s) per unit. Packaging conditions are separate from the order minimum.
                        </p>
                      )}
                      {classification === 'whole_cake' && (
                        <p className="mt-1 text-xs font-medium text-slate-600">
                          Whole cakes have a one-cake minimum and do not count toward the six-serving order minimum.
                        </p>
                      )}
                      {classification === 'needs_review' && (
                        <p className="mt-1 text-xs font-medium text-amber-800">
                          Tax treatment is awaiting seller review.
                        </p>
                      )}

                      <IngredientDisclosure ingredients={offering.ingredients} />
                      <AllergenLabels allergens={offering.allergens} />

                      {item && options.length>0 && (
                        <>
                          <div
                            className="mt-5 grid grid-cols-1 gap-2 border-t border-indigo-100 pt-5 sm:grid-cols-2"
                            onClick={(clickEvent)=>clickEvent.stopPropagation()}
                          >
                            <button
                              type="button"
                              onClick={()=>updateCustomization(offering,{
                                ...item,
                                customization_mode:'same',
                                customization_groups:[
                                  createGroup(item.quantity,item.selection)
                                ]
                              })}
                              className={`rounded-xl border p-3 text-left text-xs font-black ${
                                item.customization_mode==='split'
                                  ? 'border-slate-100 bg-white text-slate-500'
                                  : 'border-indigo-600 bg-indigo-50 text-indigo-700'
                              }`}
                            >
                              Customize all the same
                            </button>

                            <button
                              type="button"
                              onClick={()=>updateCustomization(offering,{
                                ...item,
                                customization_mode:'split',
                                customization_groups:item.customization_groups?.length
                                  ? item.customization_groups
                                  : [createGroup(item.quantity,item.selection)]
                              })}
                              className={`rounded-xl border p-3 text-left text-xs font-black ${
                                item.customization_mode==='split'
                                  ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                                  : 'border-slate-100 bg-white text-slate-500'
                              }`}
                            >
                              Split into different customizations
                            </button>
                          </div>

                          {item.customization_mode==='split' ? (
                            <SplitGroups
                              offering={offering}
                              item={item}
                              options={options}
                              userId={user.id}
                              sellerId={seller.id}
                              onChange={(nextItem)=>updateCustomization(
                                offering,
                                nextItem
                              )}
                            />
                          ) : (
                            <div
                              className="mt-5 space-y-4 border-t border-indigo-100 pt-5"
                              onClick={(clickEvent)=>clickEvent.stopPropagation()}
                            >
                              {options.map((group)=>(
                                <div key={group.name}>
                                  <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-slate-500">
                                    {group.name}
                                    {group.required && (
                                      <span className="ml-1 text-red-500">
                                        *
                                      </span>
                                    )}
                                  </label>

                                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    {group.values.map((option)=>(
                                      <label
                                        key={option.label}
                                        className="flex items-center justify-between rounded-xl bg-white p-3 text-xs font-bold text-slate-700"
                                      >
                                        <span>
                                          <input
                                            type={
                                              group.multiple
                                                ? 'checkbox'
                                                : 'radio'
                                            }
                                            name={`${offering.id}-${group.name}`}
                                            checked={
                                              group.multiple
                                                ? Array.isArray(
                                                  item.selection[group.name]
                                                )
                                                  && item.selection[
                                                    group.name
                                                  ].includes(option.label)
                                                : item.selection[
                                                  group.name
                                                ]===option.label
                                            }
                                            onChange={()=>{
                                              if (!group.multiple) {
                                                updateSelection(
                                                  offering,
                                                  group.name,
                                                  option.label
                                                );
                                                return;
                                              }

                                              const current=Array.isArray(
                                                item.selection[group.name]
                                              )
                                                ? item.selection[group.name]
                                                : [];

                                              updateSelection(
                                                offering,
                                                group.name,
                                                current.includes(option.label)
                                                  ? current.filter(
                                                    (value)=>value!==option.label
                                                  )
                                                  : [
                                                    ...current,
                                                    option.label
                                                  ]
                                              );
                                            }}
                                            className="mr-2 text-indigo-600"
                                          />
                                          {option.label}
                                        </span>

                                        {option.price!==0 && (
                                          <span className="text-indigo-600">
                                            {option.price>0 ? '+' : ''}
                                            {formatPrice(option.price)}
                                            <span className="ml-1 text-[9px] text-slate-400">
                                              {option.pricing_mode==='once_per_order'
                                                ? 'order'
                                                : 'each'}
                                            </span>
                                          </span>
                                        )}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ))}

                              <textarea
                                value={item.special_instructions || ''}
                                onChange={(event)=>updateCustomization(
                                  offering,
                                  {
                                    ...item,
                                    special_instructions:event.target.value
                                  }
                                )}
                                rows="2"
                                placeholder="Special instructions"
                                className="w-full rounded-xl border border-slate-100 bg-white p-3 text-xs font-medium outline-none focus:ring-2 focus:ring-indigo-500"
                              />

                              <CustomizationPhotoUpload
                                userId={user.id}
                                sellerId={seller.id}
                                offeringId={offering.id}
                                groupId={`same-${offering.id}`}
                                images={item.custom_photo_urls}
                                onImagesChange={(custom_photo_urls)=>updateCustomization(
                                  offering,
                                  {...item,custom_photo_urls}
                                )}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {bakeryStatus.hasSixServingItems && (
                <div className={`rounded-2xl border p-4 text-sm font-bold ${
                  bakeryStatus.minimumMet
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
                }`}>
                  <p>
                    Qualifying servings from this seller: {bakeryStatus.qualifyingServings} / 6.
                  </p>
                  <p className="mt-1 text-xs font-medium">
                    Mix and match qualifying products from this seller. Quantities do not need to be multiples of six. Whole cakes are excluded from this minimum.
                  </p>
                </div>
              )}

              {paymentMode === 'platform' && bakeryStatus.checkoutIssue && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-900">
                  {bakeryStatus.checkoutIssue}
                </div>
              )}

              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div>
                  <label className="mb-3 block text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Fulfillment Method
                  </label>

                  <div className="grid grid-cols-2 gap-3">
                    {['pickup','delivery'].map((method)=>(
                      (method==='pickup' || seller.is_delivery_enabled) && (
                        <button
                          key={method}
                          type="button"
                          onClick={()=>setFormData({
                            ...formData,
                            deliveryMethod:method
                          })}
                          className={`rounded-2xl border p-4 text-[10px] font-black uppercase tracking-widest ${
                            formData.deliveryMethod===method
                              ? 'border-indigo-600 bg-indigo-50 text-indigo-600'
                              : 'border-slate-100 bg-slate-50 text-slate-400'
                          }`}
                        >
                          <SafeIcon
                            icon={
                              method==='delivery'
                                ? FiTruck
                                : FiMapPin
                            }
                            className="mx-auto mb-2 text-xl"
                          />
                          {method}
                        </button>
                      )
                    ))}
                  </div>
                </div>

                <div>
                  <label className="mb-3 flex h-9 items-center text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Preferred Date
                    {Number(seller.min_order_window || 0)>0 && (
                      <span className="ml-2 normal-case text-indigo-500">
                        ({seller.min_order_window} day notice)
                      </span>
                    )}
                  </label>

                  <div className="relative">
                    <SafeIcon
                      icon={FiCalendar}
                      className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300"
                    />
                    <input
                      type="date"
                      required
                      min={minDate}
                      value={formData.requestedDate}
                      onChange={(changeEvent)=>{
                        const requestedDate=changeEvent.target.value;

                        if (
                          !effectiveValidDates.includes(requestedDate)
                          || !isOrderDateAllowed({
                            date:requestedDate,
                            minOrderWindow:seller.min_order_window,
                            weeklyAvailability:seller.weekly_availability,
                            blackoutDates:seller.blackout_dates
                          })
                        ) {
                          setError('Choose an order date that meets the seller availability and minimum notice.');
                          return;
                        }

                        setError('');
                        setFormData({
                          ...formData,
                          requestedDate,
                          requestedTime:''
                        });
                      }}
                      className="w-full rounded-2xl bg-slate-50 py-4 pl-12 pr-5 font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  {!effectiveValidDates.length && (
                    <p className="mt-2 text-xs font-medium text-amber-700">
                      No order dates are available in the next year.
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="requested-fulfillment-time"
                    className="mb-3 flex h-9 items-center text-[10px] font-black uppercase tracking-widest text-slate-400"
                  >
                    {formData.deliveryMethod==='pickup'
                      ? 'Pickup Time'
                      : 'Delivery Time'}
                  </label>
                  <select
                    id="requested-fulfillment-time"
                    required
                    value={formData.requestedTime}
                    onChange={(changeEvent)=>setFormData({
                      ...formData,
                      requestedTime:changeEvent.target.value
                    })}
                    className="w-full rounded-2xl bg-slate-50 px-5 py-4 font-bold outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="" disabled>
                      Select a time
                    </option>
                    {effectiveValidTimes.map(({value,label})=>(
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {formData.requestedDate && !effectiveValidTimes.length && (
                    <p className="mt-2 text-xs font-medium text-amber-700">
                      No times are available on this date.
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t border-slate-50 pt-6">
                <div className="mb-4 flex justify-between text-xs font-bold uppercase tracking-widest text-slate-400">
                  <span>Subtotal</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>

                {deliveryFee>0 && (
                  <div className="mb-4 flex justify-between text-xs font-bold uppercase tracking-widest text-indigo-600">
                    <span>Delivery</span>
                    <span>{formatPrice(deliveryFee)}</span>
                  </div>
                )}

                <div className="mb-6 rounded-2xl bg-indigo-50 p-4 text-xs font-black leading-relaxed text-indigo-700">
                  Applicable taxes will be calculated at checkout.
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs font-black uppercase tracking-widest text-slate-900">
                    Subtotal before applicable taxes
                  </span>
                  <span className="text-3xl font-black text-indigo-600">
                    {formatPrice(totalPrice)}
                  </span>
                </div>
              </div>

              <div className="flex items-start rounded-2xl bg-slate-50 p-4">
                <SafeIcon
                  icon={FiShield}
                  className="mr-3 text-slate-400"
                />
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  By continuing, you agree to SohLocal's
                  <Link
                    to="/terms"
                    className="mx-1 text-indigo-600 underline"
                  >
                    Terms
                  </Link>
                  and
                  <Link
                    to="/privacy"
                    className="ml-1 text-indigo-600 underline"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>

              <button
                type="submit"
                disabled={
                  loading
                  || !Object.keys(selectedItems).length
                }
                className="w-full rounded-[1.5rem] bg-indigo-600 py-5 text-sm font-black uppercase tracking-[0.2em] text-white shadow-xl shadow-indigo-100 disabled:opacity-50"
              >
                {loading ? (
                  <SafeIcon
                    icon={FiLoader}
                    className="mx-auto animate-spin"
                  />
                ) : (
                  paymentMode==='platform'
                    ? 'Continue to Secure Checkout'
                    : 'Send Order Request'
                )}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default OrderFormModal;
